import { useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { X, Check, RotateCcw, ArrowRight, Paintbrush } from 'lucide-react';

interface DocumentRedactorModalProps {
  /** Arquivo de imagem selecionado */
  file: File;
  /** Título do modal (Frente ou Verso) */
  title: string;
  /** Callback com a imagem final censurada em base64 */
  onCompleted: (base64: string) => void;
  /** Fechar sem salvar */
  onCancel: () => void;
}

/**
 * Modal interativo para redação e censura de documentos (CPF e Data de Nascimento).
 * Guia o usuário em etapas para selecionar CPF e Data de Nascimento, aplicando
 * tarjas pretas parciais de segurança no frontend.
 *
 * @internal true
 * @owner gestao
 * @updated 2026-06-23
 */
export function DocumentRedactorModal({ file, title, onCompleted, onCancel }: DocumentRedactorModalProps) {
  // Etapas: 1 = Selecionar CPF, 2 = Selecionar Nascimento, 3 = Tarja Livre / Confirmar
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [crop, setCrop] = useState<Crop>();
  const [imgSrc, setImgSrc] = useState<string>(() => URL.createObjectURL(file));
  
  const imgRef = useRef<HTMLImageElement>(null);
  
  // Estado para desenho livre (Tarja manual) no Passo 3
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  /** Centralizar crop inicial */
  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const cropWidth = Math.min(width * 0.6, 200);
    const cropHeight = Math.min(height * 0.15, 40);
    const newCrop = centerCrop(
      makeAspectCrop({ unit: 'px', width: cropWidth, height: cropHeight }, 5, width, height),
      width,
      height,
    );
    setCrop(newCrop);
  }, []);

  /** Aplica a tarja preta na região selecionada e avança de etapa */
  const applyRedaction = useCallback(() => {
    const image = imgRef.current;
    if (!image || !crop) return;

    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 1. Desenhar a imagem atual no canvas
    ctx.drawImage(image, 0, 0);

    // Escala entre imagem natural (física) e renderizada na tela
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const cropX = crop.x * scaleX;
    const cropY = crop.y * scaleY;
    const cropW = crop.width * scaleX;
    const cropH = crop.height * scaleY;

    if (step === 1) {
      // Regra CPF: Grifar o meio (60% horizontal), mantendo pontas esquerda/direita legíveis
      const sideWidth = cropW * 0.2;
      const maskWidth = cropW * 0.6;
      ctx.fillStyle = '#000000';
      ctx.fillRect(cropX + sideWidth, cropY, maskWidth, cropH);
    } else if (step === 2) {
      // Regra Data Nascimento: Ocultar o meio (60% horizontal)
      const sideWidth = cropW * 0.2;
      const maskWidth = cropW * 0.6;
      ctx.fillStyle = '#000000';
      ctx.fillRect(cropX + sideWidth, cropY, maskWidth, cropH);
    }

    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setImgSrc(dataUrl);
    setCrop(undefined);

    // Avançar etapa
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    }
  }, [crop, step]);

  // Inicializa o canvas interativo de desenho livre no Passo 3
  const initDrawCanvas = () => {
    const image = imgRef.current;
    const canvas = canvasRef.current;
    if (!image || !canvas) return;

    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(image, 0, 0);
  };

  /** Desenhar tarja livre com mouse/touch */
  const startDraw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    ctx.beginPath();
    ctx.moveTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    ctx.lineTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = Math.max(canvas.height * 0.05, 15); // Linha grossa proporcional à imagem
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  const endDraw = () => {
    setIsDrawing(false);
  };

  const handleConfirmFinal = () => {
    if (step === 3 && canvasRef.current) {
      const base64 = canvasRef.current.toDataURL('image/jpeg', 0.85);
      onCompleted(base64);
    } else {
      // Se pular o passo 3 sem rabiscar
      onCompleted(imgSrc);
    }
  };

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onCancel}>
      <div style={{ background: '#1c1c1e', borderRadius: 24, padding: '24px 28px', maxWidth: 520, width: '92vw', boxShadow: '0 12px 40px rgba(0,0,0,.5)', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f5f7' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#ffffff' }}>Redigir Documento: {title}</h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#98989d' }}>
              {step === 1 ? 'Etapa 1 de 3: Selecione a área do CPF' : step === 2 ? 'Etapa 2 de 3: Selecione a área da Data de Nascimento' : 'Etapa 3 de 3: Revise ou adicione tarjas livres'}
            </p>
          </div>
          <button onClick={onCancel} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', cursor: 'pointer', color: '#aeaeaf', padding: 6, display: 'flex' }}>
            <X size={18} />
          </button>
        </div>

        {/* Action instruction alert */}
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', fontSize: '0.8rem', color: '#93c5fd', marginBottom: 16 }}>
          {step === 1 && 'Arraste a caixa de marcação vermelha sobre o número do CPF. O sistema irá ocultar apenas os dígitos centrais.'}
          {step === 2 && 'Arraste a caixa de marcação vermelha sobre a Data de Nascimento para ocultar parcialmente a data.'}
          {step === 3 && 'Use o mouse/dedo para pintar tarjas pretas adicionais se houver outras informações confidenciais (ex: RG, foto).'}
        </div>

        {/* Editor Area */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16, background: '#121214', borderRadius: 14, padding: 12, border: '1px solid rgba(255,255,255,0.04)', minHeight: 220, position: 'relative' }}>
          {step < 3 ? (
            <ReactCrop
              crop={crop}
              onChange={c => setCrop(c)}
              minWidth={30}
              minHeight={15}
            >
              <img
                ref={imgRef}
                src={imgSrc}
                alt="Documento"
                onLoad={onImageLoad}
                style={{ maxHeight: 340, maxWidth: '100%', display: 'block', borderRadius: 8 }}
                crossOrigin="anonymous"
              />
            </ReactCrop>
          ) : (
            <div style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
              <canvas
                ref={canvasRef}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                style={{ maxHeight: 340, maxWidth: '100%', cursor: 'crosshair', display: 'block', borderRadius: 8, background: '#121214' }}
              />
              <img
                ref={imgRef}
                src={imgSrc}
                alt="Hidden source"
                onLoad={initDrawCanvas}
                style={{ display: 'none' }}
              />
            </div>
          )}
        </div>

        {/* Footer / Buttons */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button 
            type="button"
            onClick={onCancel} 
            style={{ padding: '10px 18px', background: 'rgba(255,255,255,0.05)', color: '#e5e5ea', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
          >
            Cancelar
          </button>

          {step < 3 ? (
            <button 
              type="button"
              disabled={!crop?.width}
              onClick={applyRedaction} 
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', background: '#3a86f8', color: 'white', border: 'none', borderRadius: 12, fontSize: '0.85rem', fontWeight: 700, cursor: !crop?.width ? 'not-allowed' : 'pointer', opacity: !crop?.width ? 0.6 : 1 }}
            >
              Censurar Campo <ArrowRight size={14} />
            </button>
          ) : (
            <button 
              type="button"
              onClick={handleConfirmFinal} 
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 22px', background: '#34c759', color: 'white', border: 'none', borderRadius: 12, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
            >
              Finalizar e Salvar <Check size={14} />
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
